import { Dispatch, useCallback, useEffect } from 'react';
import { User, UserManager } from 'oidc-client-ts';

import { Action, AuthActions } from '../auth-provider.types';

/**
 * Register oidc-client-ts.userManager.events
 *
 */
export const useUserManagerEvents = (
  userManager: UserManager,
  dispatch: Dispatch<Action>,
) => {
  const handleUserLoaded = useCallback(
    (user: User) => {
      dispatch({ type: AuthActions.UserLoaded, user });
    },
    [dispatch],
  );

  const handleAccessTokenExpiring = useCallback(() => {
    dispatch({ type: AuthActions.TokenExpiring });
  }, [dispatch]);

  const handleAccessTokenExpired = useCallback(() => {
    dispatch({ type: AuthActions.TokenExpired });
  }, [dispatch]);

  const handleUserUnloaded = useCallback(() => {
    dispatch({ type: AuthActions.UserUnloaded });
  }, [dispatch]);

  const handleSilentRenewError = useCallback(
    (error: Error) => {
      dispatch({ type: AuthActions.Error, error });
    },
    [dispatch],
  );

  /**
   * `UserSignedOut` is deliberately not handled here. `monitorSession`'s
   * `check_session_iframe` polling raises it on a genuine IdP sign-out, but
   * also on an unrelated false positive - most commonly the iframe's
   * cross-origin cookie read being blocked (third-party cookie blocking, or a
   * local app origin that isn't same-site with the IdP). Unconditionally
   * dropping the user here on every fire had exactly that failure mode: it
   * cleared the stored refresh token on a false positive, forcing a full
   * IdP redirect that the still-live IdP session then approved instantly,
   * re-triggering the same false positive in a loop. `useCrossAppAuthListener`
   * (`@xemplo/auth-helper`) owns this event instead, confirming genuineness by
   * attempting a refresh-token renewal - a plain network call, independent of
   * the iframe - before treating it as a real sign-out.
   */
  useEffect(() => {
    // event UserLoaded (e.g. initial load, silent renew success)
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);

    // event UserUnloaded (e.g. removeUser)
    userManager.events.addUserUnloaded(handleUserUnloaded);

    // event SilentRenewError (silent renew error)
    userManager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [
    dispatch,
    handleAccessTokenExpired,
    handleAccessTokenExpiring,
    handleSilentRenewError,
    handleUserLoaded,
    handleUserUnloaded,
    userManager,
  ]);
};
